import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { TimeAndBillingService } from 'src/app/time-and-billing/time-and-billing.service';
import { CreatePage as PaymentCreatePage } from 'src/app/payments/create/create.page'
import * as dayjs from 'dayjs';
import { ApplyUseProjectDates, TaskDueDateLabel, TaskStartDateLabel, UsesProjectDates } from '../../services/task-project-dates.util';
import { SVGGantt, CanvasGantt, StrGantt } from 'gantt';
import { CreatePage as TimeAndBillingCreatePage } from 'src/app/time-and-billing/create/create.page';
import { SelectComponent as TagsSelectPage } from 'src/app/components/tags/select/select.component';
import { ManagerComponent as GroupsManagerPage } from 'src/app/components/groups/manager/manager.component';
import { SelectPage as TeamMemberSelectPage } from 'src/app/team-members/select/select.page';
import { CreatePage } from 'src/app/tasks/create/create.page';
import { DetailsPage as TaskDetailsPage } from 'src/app/tasks/details/details.page';
import { DetailsPage as StepDetailsPage } from 'src/app/steps/details/details.page';
import { TaskStatus } from 'src/app/components/tasks/TaskStatus';
import { ProjectHealthService } from 'src/app/api/project-health.service';

@Component({
  selector: 'app-details',
  templateUrl: './details.page.html',
  styleUrls: ['./details.page.scss'],
})
export class DetailsPage implements OnInit, OnDestroy {
  UsesProjectDates = UsesProjectDates;
  TaskStartDateLabel = TaskStartDateLabel;
  TaskDueDateLabel = TaskDueDateLabel;
  @ViewChild('svgRoot', { static: true }) svgRoot: ElementRef;
  //Takes in both the project ID and the project itself
  @Input() guid: string;
  project: any = null;
  activeIndex = 'Details';
  // Opened from an @mention notification: the comment to scroll to and highlight on the Comments tab
  @Input() focus_comment: any = null;
  refresh = false;
  current_user: any = null;
  edit_mode = false;
  edit_task_mode = false;
  selected_billing_group: any;
  billing_groups: any;
  team_members_valid = true;
  team_members: any = [];
  task_templates: any;
  selected_task_template: any;
  show_paging = true;
  max_per_page = 25;
  today: any;

  task_copy = "";
  current_edit_task_copy = "";

  comment: any = {
    reply_to: null,
    reply_to_text: null,
    comment: '',
    update: false
  }
  svg_image = "";
  no_project = false;
  project_json = '';
  projectInternalStatuses: any[] = [];
  open_missing_info_count: number = 0;
  total_comment_count: number = 0;
  comment_filter: string = 'Project';
  all_comments: any[] = [];
  task_comments: any[] = [];
  step_comments: any[] = [];
  project_comments: any[] = [];
  comments_loading: boolean = false;
  active_reply_guid: string = null;
  reply_comment: any = { reply_to: null, reply_to_text: null, reply_to_name: null, comment: '', update: false };
    rowColourPresets: string[] = ['#FFFFFF', '#90caf9', '#ffe082', '#81c784', '#f8bbd0', '#4dd0e1', '#ce93d8', '#dce775', '#ffb74d', '#a5d6a7'];
  customRowColour = '#FFFFFF';
  isCustomRowColourSelected = false;

  constructor(private ref: ChangeDetectorRef, private apiService: ApiService, private modalController: ModalController, public globalService: GlobalService, private elementRef: ElementRef, private alertController: AlertController, public projectHealthService: ProjectHealthService, private timeAndBillingService: TimeAndBillingService) {

  }

  // Delegates to ProjectHealthService.GetHealthReasons - same plain-English reasons shown in the
  // projects table's health tooltip (see table.component.ts's GetHealthTooltipText), so the two
  // stay in sync automatically as factors are added/changed instead of drifting apart.
  GetHealthTooltip(): string {
    const result = this.projectHealthService.Evaluate(this.project);
    if (!result) return '';
    const reasons = this.projectHealthService.GetHealthReasons(result, this.projectHealthService.GetCachedConfig());
    const reason_list = reasons.map(r => `• ${r}`).join('<br>');
    return `Score: ${Math.round(result.score)} — ${result.level.label}<br>${reason_list}`;
  }

  private time_changes_subscription: Subscription;

  ngOnInit() {
    this.current_user = this.globalService.GetCurrentUser();
    this.GetProjectData();
    // A time entry was created or deleted somewhere (the timer, a task...) - reload the list if it's showing
    this.time_changes_subscription = this.timeAndBillingService.changes$.subscribe(() => {
      if (this.activeIndex == 'Time Entries') this.LoadTimeEntries();
    });
  }

  ngOnDestroy() {
    this.time_changes_subscription?.unsubscribe();
  }

  async Log() {
    console.log(this.project);
  }

  async StartEditMode() {
    this.project_json = JSON.stringify(this.project);
    this.edit_mode = true;
  }

  async CancelEditMode() {
    this.project = JSON.parse(this.project_json);
    this.project_json = '';
    this.edit_mode = false;
  }

  async GetProjectData() {
    await this.GetTeamMembers();
    const statusData: any = await this.apiService.get('tags/projects_internal_status');
    if (statusData.success) this.projectInternalStatuses = statusData.data || [];
    let data: any = await this.apiService.get(`projects/${this.guid}`);
    if (data.success) {
      this.project = data.data[0];
      if (!this.project.groups) this.project.groups = [];
      if (!this.project.tags) this.project.tags = [];
      if (!this.project.tasks) this.project.tasks = [];
      if (!this.project.team_members) this.project.team_members = [];
      if (!this.project.time_and_billing) this.project.time_and_billing = [];
      this.project.internal_start_date = this.globalService.ConvertDateFromBackend(this.project.internal_start_date);
      this.project.internal_end_date = this.globalService.ConvertDateFromBackend(this.project.internal_end_date);
      this.project.external_end_date = this.globalService.ConvertDateFromBackend(this.project.external_end_date);
      this.project.external_start_date = this.globalService.ConvertDateFromBackend(this.project.external_start_date);
      this.project.period_start_date = this.globalService.ConvertDateFromBackend(this.project.period_start_date);
      this.project.period_end_date = this.globalService.ConvertDateFromBackend(this.project.period_end_date);
      console.log("PROJECT: ", this.project);
    }
    else {
      this.no_project = true;
    }

    this.GetOpenMissingInfoCount();
    this.GetTotalCommentCount();
    if (this.activeIndex === 'Comments') this.GetProjectComments();
    //this.SetupGnatt();
    //this.GetBillingGroups();
  }
  async GetBillingGroups() {
    let data: any = await this.apiService.get('billing_groups');
    if (data.success) {
      this.billing_groups = data.data;
      this.selected_billing_group = this.billing_groups[this.globalService.GetArrayIndex(this.billing_groups, this.project.default_billing_group_guid)];
    }
  }

  async GetTotalCommentCount() {
    const data: any = await this.apiService.get(`projects/${this.guid}/all_comments?count=1`);
    if (data.success && data.data?.[0]) this.total_comment_count = data.data[0].count;
  }

  async GetProjectComments() {
    this.comments_loading = true;
    this.comment_filter = 'Project';

    const result: any = await this.apiService.get(`projects/${this.guid}/all_comments`);
    const all: any[] = result.success ? (result.data || []) : [];

    this.project_comments = [];
    this.task_comments = [];
    this.step_comments = [];

    for (const c of all) {
      if (c.source_type === 'task') this.task_comments.push(c);
      else if (c.source_type === 'step') this.step_comments.push(c);
      else this.project_comments.push(c);
    }

    // Format users field by parsing JSON strings
    const formatComments = (comments: any[]) => {
      for (const comment of comments) {
        if (comment.users && typeof comment.users === 'string') {
          comment.users = JSON.parse(comment.users);
        }
      }
    };

    formatComments(this.project_comments);
    formatComments(this.task_comments);
    formatComments(this.step_comments);

    this.project.comments = this.project_comments;
    this.all_comments = all;
    this.total_comment_count = all.length;
    this.comments_loading = false;
  }

  GetFilteredComments() {
    if (this.comment_filter === 'Tasks') return this.task_comments;
    if (this.comment_filter === 'Actions') return this.step_comments;
    if (this.comment_filter === 'Project') return this.project_comments;
    return this.all_comments;
  }

  SetCommentFilter(filter: string) {
    this.comment_filter = filter;
    this.CancelInlineReply();
  }

  StartInlineReply(comment: any) {
    this.active_reply_guid = comment.guid;
    this.reply_comment = {
      reply_to: comment.guid,
      reply_to_text: comment.comment,
      reply_to_name: comment.users?.[0]?.display_name,
      comment: '',
      update: false
    };
  }

  CancelInlineReply() {
    this.active_reply_guid = null;
    this.reply_comment = { reply_to: null, reply_to_text: null, reply_to_name: null, comment: '', update: false };
  }

  async SubmitInlineReply(comment: any) {
    let url = 'projects/comments';
    let guid = this.project.guid;
    if (comment.source_type === 'task') { url = 'tasks/comments'; guid = comment.source_guid; }
    else if (comment.source_type === 'step') { url = 'steps/comments'; guid = comment.source_guid; }

    const data: any = await this.apiService.post(`${url}/${guid}`, this.reply_comment);
    if (data.success) {
      const new_comment: any = data.data[0];
      new_comment.users = [{ display_name: this.current_user.display_name, guid: this.current_user.guid }];
      new_comment.comment = JSON.parse(JSON.stringify(this.reply_comment.comment));
      new_comment.reply_to_guid = this.reply_comment.reply_to;
      new_comment.source_type = comment.source_type;
      new_comment.source_title = comment.source_title;
      new_comment.source_guid = comment.source_guid;
      new_comment.task_title = comment.task_title;

      if (comment.source_type === 'task') this.task_comments.unshift(new_comment);
      else if (comment.source_type === 'step') this.step_comments.unshift(new_comment);
      else { this.project_comments.unshift(new_comment); if (this.project.comments) this.project.comments.unshift(new_comment); }

      this.all_comments.unshift(new_comment);
      this.total_comment_count = this.all_comments.length;
      this.CancelInlineReply();
    }
    this.globalService.presentToastMessage(data.message, data.success);
  }

  async GetOpenMissingInfoCount() {
    const data: any = await this.apiService.get(`missing_info?completed=0&project=${this.guid}&count=1`);
    if (data.success && data.data) this.open_missing_info_count = data.data[0].count;
    else this.open_missing_info_count = 0;

    console.log("Open Missing Info Count: ", this.open_missing_info_count);
  }

  async GoToStep(val) {
    this.activeIndex = val;
    if (val == 'Comments') {
      this.GetProjectComments();
    }
    if (val == 'Missing Info') {
      this.GetOpenMissingInfoCount();
    }
    if (val == 'Time Entries') {
      this.LoadTimeEntries();
    }
  }

  async LoadTimeEntries() {
    this.project.time_and_billing = await this.timeAndBillingService.GetEntries({ project_guid: this.guid });
  }

  //This is fired when a step was updated
  //When its changed we want to refresh the project list because other projects might have been completed by this project being completed
  stepModified(eventData: { data: any }) {
    this.project = eventData.data;
    this.refresh = true;
  }

  Dismiss() {
    this.modalController.dismiss({ refresh: this.refresh });

  }


  async AddBillableTime() {

  }

  async AddPayment() {
    const modal = await this.modalController.create({
      component: PaymentCreatePage,
      cssClass: 'large-modal',
      componentProps: {
        project: this.project,
      },
    })
    modal.onDidDismiss().then((data: any) => {
      this.GetProjectData();
    });
    return await modal.present()




  }

  async SetupGnatt() {
    //document.getElementById('svg-root').innerHTML = "";
    //Formatt the projects to fit the gnatt format
    let formatted_projects = [];
    let formatted_project = {
      id: this.project.guid,
      type: 'group',
      text: this.project.title,
      start: dayjs(this.project.internal_start_date).toDate(),
      end: dayjs(this.project.internal_end_date).toDate(),
      percent: this.project.progress,
      project: this.project,
      is_fake: this.project.is_fake,
      is_project: true
    }
    formatted_projects.push(formatted_project);
    this.project.tasks.forEach(task => {
      let formatted_task = null;
      formatted_task = {
        id: task.guid,
        parent: this.project.guid,
        text: task.title,
        start: dayjs(task.due_date).toDate(),
        end: dayjs(task.due_date).endOf('day').toDate(),
        percent: task.progress,
        task: task,
        project: this.project,
        is_fake: this.project.is_fake,
        is_project: false
      }

      formatted_projects.push(formatted_task);
    });
    console.log(formatted_projects);


    //const svgRootElement = this.svgRoot.nativeElement;
    const divElement = document.createElement('div');
    divElement.setAttribute("id", "hidden-svg");
    divElement.setAttribute("style", "display: none;");
    const htmlRoot = document.querySelector('html');
    htmlRoot.appendChild(divElement);
    console.log(divElement.innerHTML)

    new SVGGantt(divElement, formatted_projects, {
      viewMode: 'day',
      onClick: (item: any) => { }
    });
    //Set the inner HTML of the element 

    console.log(divElement.innerHTML);
    this.svg_image = divElement.innerHTML;
  }


  async AddtimeEntry() {
    const modal = await this.modalController.create({
      component: TimeAndBillingCreatePage,
      cssClass: 'large-modal',
      componentProps: { project: this.project },
    });
    modal.onDidDismiss().then(() => this.LoadTimeEntries());
    return await modal.present();
  }


  //Project Edit Mode
  async ChangeTaskDates(task) {
    let dates_valid = true;
    if (task.relative_date) {
      if (task.relative_to_start) {
        task.start_date = dayjs(this.project.internal_start_date).add(task.start_date_relative, task.start_date_relative_unit).format('YYYY-MM-DD');
        task.due_date = dayjs(this.project.internal_start_date).add(task.due_date_relative, task.due_date_relative_unit).format('YYYY-MM-DD');
      }
      else {
        task.start_date = dayjs(this.project.internal_end_date).add(task.start_date_relative * -1, task.start_date_relative_unit).format('YYYY-MM-DD');
        task.due_date = dayjs(this.project.internal_end_date).add(task.due_date_relative * -1, task.due_date_relative_unit).format('YYYY-MM-DD');
      }
      ApplyUseProjectDates(task, this.project.internal_start_date, this.project.internal_end_date);
      if (task.due_date > this.project.internal_end_date) {
        if (true) {
          task.due_date = this.project.internal_end_date;
        }
        else {
          dates_valid = false;
          this.globalService.presentToastMessage(`Task: ${task.title} is due after the project is due.`, false);
        }

      }
      if (task.due_date < this.project.internal_start_date) {
        if (true) {
          task.due_date = this.project.internal_start_date;
        }
        else {
          dates_valid = false;
          this.globalService.presentToastMessage(`Task: ${task.title} is due before the project is started.`, false);
        }
      }
      // Action due dates are no longer used - just keep the stored value inside its task's dates.
      task.steps.forEach(step => {
        if (step.relative_date) {
          step.due_date = dayjs(task.start_date).add(step.due_date_relative, 'day').format('YYYY-MM-DD');
          if (step.due_date > task.due_date) step.due_date = task.due_date;
          if (step.due_date < task.start_date) step.due_date = task.start_date;
        }
      });
    }
    else {
      if (task.due_date > this.project.internal_end_date) {
        dates_valid = false;
        this.globalService.presentToastMessage(`Task: ${task.title} is due after the project is due.`, false);
      }
      if (task.due_date < this.project.internal_start_date) {
        dates_valid = false;
        this.globalService.presentToastMessage(`Task: ${task.title} is due before the project is started.`, false);
      }
    }


  }

  ProjectDateConfilct(date_in) {
    date_in = dayjs(date_in).format('YYYY-MM-DD');
    if ((date_in > this.project.internal_end_date) || date_in < this.project.internal_start_date) {
      console.log("Checking Date Conflict: ");
      console.log(date_in);
      console.log(this.project.internal_start_date);
      console.log(this.project.internal_end_date);
      console.log("!!!!")
      return true;
    }

  }

  HasProjectDateConflict() {
    let has_conflict = false;
    for (let i = 0; i < this.project.tasks.length; i++) {
      let task = this.project.tasks[i];
      if (this.ProjectDateConfilct(task.due_date)) {
        has_conflict = true;
        break;
      }
    }
    return has_conflict;

  }

  //Remove from arrays
  async RemoveTag(tag) {
    this.globalService.RemoveFromArray(this.project.tags, tag.guid);
  }


  async presentSelectTagsModal() {
    let tags_copy = JSON.stringify(this.project.tags);
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'projects',
        selected_tags: this.project.tags
      }
    })
    modal.onDidDismiss().then((data: any) => {

      if (data && data.data) {
        tags_copy = JSON.parse(tags_copy);
      }
      else {
        this.project.tags = [...JSON.parse(tags_copy)];
      }
    });
    return await modal.present()
  }


  async presentSelectGroupsModal() {
    const modal = await this.modalController.create({
      component: GroupsManagerPage,
      cssClass: 'medium-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'projects',
        selection_mode: true,
        max_selection: 999,
        selected_groups: [...this.project.groups],
        modal_title: 'Select Groups'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        this.project.groups = data.data;
      }
    });
    return await modal.present();
  }

  async ProjectInternalStatusChange() {
    this.apiService.put(`projects/${this.project.guid}`, { internal_status: this.project.internal_status });
  }

  GetInternalStatusByGuid(guid: string) {
    if (!guid || !this.projectInternalStatuses?.length) return null;
    return this.projectInternalStatuses.find(s => s.guid === guid) || null;
  }

  // estimated_time/estimated_time_remaining are stored as raw minutes - display-only, not editable
  // here (they're derived from the project's tasks, see Projects.CreateProjectMethod/Tasks.cs).
  FormatEstimatedTime(minutes: number): string {
    const total = Number(minutes) || 0;
    const hours = Math.floor(total / 60);
    const mins = total % 60;
    return `${hours}h ${mins}m`;
  }

  get projectInternalStatusOptions() {
    return [
      { value: '', label: 'None', description: 'No internal status set.' },
      ...this.projectInternalStatuses.map(s => ({ value: s.guid, label: s.title, description: s.description, color: s.colour }))
    ];
  }

  async UpdateProject() {
    this.globalService.presentLoading("Updating Project", 99999999);
    //this.project.default_billing_group = this.selected_billing_group.guid;
    let data: any = await this.apiService.put(`projects/${this.project.guid}`, this.project);
    this.refresh = true;
    console.log(data);
    if (data.success) this.edit_mode = false;
    this.globalService.presentToastMessage(data.message, data.success);
    this.globalService.dismissLoading();
  }

  async UpdateProjectAssignmentSettings() {
    await this.globalService.presentLoading('Applying assignment settings to all tasks...', 99999999);
    const projectData: any = await this.apiService.put(`projects/${this.project.guid}`, {
      assignment_settings: this.project.assignment_settings
    });
    if (projectData.success) {
      for (const task of this.project.tasks || []) {
        task.assignment_settings = this.project.assignment_settings;
        await this.apiService.put(`tasks/${task.guid}`, {
          assignment_settings: task.assignment_settings
        });
      }
      this.globalService.presentToastMessage('Assignment settings applied to all tasks.', true);
    } else {
      this.globalService.presentToastMessage(projectData.message, false);
    }
    await this.globalService.dismissLoading();
  }


  async StartEditTaskMode() {
    this.globalService.presentLoading("Switiching to Edit Mode", 99999999);
    for (let i = 0; i < this.project.tasks.length; i++) {
      // Task processing code here
      let data: any = await this.apiService.get(`tasks/${this.project.tasks[i].guid}`);
      if (data.success) {
        this.project.tasks[i] = data.data[0];
      }
    }

    for (let i = 0; i < this.project.team_members.length; i++) {
      let data: any = await this.apiService.get(`team_members/${this.project.team_members[i].guid}`);
      if (data.success) {
        this.project.team_members[i] = data.data[0];
      }
    }
    console.log("Updated Tasks With Details: ", this.project.tasks);
    //We need to set it after because then we have the details of the task included
    this.task_copy = JSON.stringify(this.project.tasks);
    await this.GetTaskTemplates();
    this.edit_task_mode = true;
    this.globalService.dismissLoading();
  }

  async CancelEditTaskMode() {
    this.project.tasks = JSON.parse(this.task_copy);
    this.task_copy = "";
    this.edit_task_mode = false;
  }



  async UpdateProjectTasks() {
    await this.globalService.presentLoading("Updating Project Tasks", 99999999);
    let old_task_data: any = JSON.parse(this.task_copy);
    let has_new = false;
    let has_deleted = false;

    for (let i = 0; i < this.project.tasks.length; i++) {
      const task = this.project.tasks[i];
      console.log("Upated task: ", task);
      let index = this.globalService.GetArrayIndex(old_task_data, task.guid);
      if (index !== -1) {
        // Task exists in old data, check if it has changed
        if (JSON.stringify(task) !== JSON.stringify(old_task_data[index])) {
          // Log the differences
          const oldTask = old_task_data[index];
          const differences = {};
          
          Object.keys(task).forEach(key => {
            if (JSON.stringify(task[key]) !== JSON.stringify(oldTask[key])) {
              differences[key] = {
          old: oldTask[key],
          new: task[key]
              };
            }
          });
          
            console.log("Task Differences: ", differences);
            
            // Check if only steps have changed
            const changedKeys = Object.keys(differences);
            const onlyStepsChanged = changedKeys.length === 1 && changedKeys[0] === 'steps';
            
            if (!onlyStepsChanged) {
              task.project_guid = this.project.guid;
              task.order_num = i;
              console.log("Task Updated: ", task);
              await this.apiService.put(`tasks/${task.guid}`, task);
            }
            
            // Handle step changes
            if (differences['steps']) {
              const oldSteps = old_task_data[index].steps || [];
              const newSteps = task.steps || [];
              
              // Check for modified and new steps
              for (let j = 0; j < newSteps.length; j++) {
              const step = newSteps[j];
              const oldStepIndex = oldSteps.findIndex(s => s.guid === step.guid);
              
              if (oldStepIndex !== -1) {
                // Step exists, check if modified
                if (JSON.stringify(step) !== JSON.stringify(oldSteps[oldStepIndex])) {
                console.log("Step Updated: ", step);
                step.project_guid = this.project.guid;
                step.task_guid = task.guid;
                step.task_start_date = task.start_date;
                step.task_end_date = task.due_date;
                step.step_data = JSON.stringify(step.data);
                step.order_num = j;
                await this.apiService.put(`steps/${step.guid}`, step);
                }
              } else {
                // New step
                step.task_guid = task.guid;
                step.order_num = j;
                step.project_guid = this.project.guid;
                step.task_guid = task.guid;
                step.task_start_date = task.start_date;
                step.task_end_date = task.due_date;
                step.step_data = JSON.stringify(step.data);
                console.log("New Step Found: ", step);
                await this.apiService.post(`steps`, step);
              }
              }
              
              // Check for deleted steps
              for (let j = 0; j < oldSteps.length; j++) {
              const step = oldSteps[j];
              const newStepIndex = newSteps.findIndex(s => s.guid === step.guid);
              
              if (newStepIndex === -1) {
                console.log("Deleting Step: ", step);
                await this.apiService.delete(`steps/${step.guid}`, null);
              }
              }
            }
        }
      }
    }

    for (let i = 0; i < this.project.tasks.length; i++) {
      const task = this.project.tasks[i];
      //Try and find the task in the old_task_data
      let index = this.globalService.GetArrayIndex(old_task_data, task.guid);
      if (index == -1) {
        task.project_guid = this.project.guid;
        task.order_num = i;
        console.log("New Task Found: ", task);
        await this.apiService.post(`tasks`, task);
        //Post to the projects to add the task
      }
    }

    for (let i = 0; i < old_task_data.length; i++) {
      const task = old_task_data[i];
      //Check if the task has been deleted
      let index = this.globalService.GetArrayIndex(this.project.tasks, task.guid);
      if (index == -1) {
        console.log("Deleting Task: ", task);
        //Delete the task from the project
        await this.apiService.delete(`tasks/${task.guid}`, null);
      }
    }


    
    this.GetProjectData();
    this.globalService.dismissLoading();
    this.edit_task_mode = false;

  }


  async presentSelectTeamMembers() {
    let team_member_copy = JSON.stringify(this.project.team_members);
    const modal = await this.modalController.create({
      component: TeamMemberSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        selecting_for_company: false,
        selecting_for_projects: true,
        selected_team_members: this.project.team_members,
        remove_team_members: this.project.team_members,
        in_team_members: [],
        target_header: 'Currently Selected'
      }
    })
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        team_member_copy = JSON.parse(team_member_copy);
      }
      else {
        this.project.team_members = [...JSON.parse(team_member_copy)];
      }
      this.ChangeClientsOrTeamMembers();
    });
    return await modal.present()

  }



  async RemoveTeamMember(tm) {
    this.globalService.RemoveFromArray(this.project.team_members, tm.guid);
    this.ChangeClientsOrTeamMembers();

  }

  async RemoveGroup(group) {
    this.globalService.RemoveFromArray(this.project.groups, group.guid);
  }

  async selectedBillingGroup() {

  }

  async presentDeleteConfirm() {

    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: 'Are you sure you would like to delete this project! This cannot be undone!',
      message: `${this.project.title}`,
      inputs: [
        {
          name: 'delete_type',
          type: 'radio',
          label: 'Just this project',
          value: '1',
          checked: true
        },
        {
          name: 'delete_type',
          type: 'radio',
          label: 'This and all future related projects',
          value: '2'
        },
        {
          name: 'delete_type',
          type: 'radio',
          label: 'This and all future and past related projects',
          value: '3'
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'OK',
          handler: async (data) => {
            this.globalService.presentLoading("Preparing Delete", 3000);
            const deleteType = data;
            let apiData: any = await this.apiService.delete(`projects/${this.project.guid}`, { delete_type: deleteType });
            this.globalService.presentToastMessage(apiData.message, apiData.success);
            await this.globalService.dismissLoading();
            this.refresh = true;
            this.Dismiss();
          }
        }
      ]
    });
    await alert.present();
    return;
  }

  async pauseProject(paused) {
    const alert = await this.alertController.create({
      backdropDismiss: false,
      header: paused ? 'Pause Project' : 'Resume Project',
      message: paused ? 'Pausing Projects will remove the clients ability to see the tasks they are needed to finish. It also removes any project tasks from auto starting and removes your ability to start or complete a project/task/step.' : 'Are you sure you want to resume this project?',
      inputs: [
        {
          name: 'pause_type',
          type: 'radio',
          label: 'Just this project',
          value: '1',
          checked: true
        },
        {
          name: 'pause_type',
          type: 'radio',
          label: 'This and all future related projects',
          value: '2'
        },
        {
          name: 'pause_type',
          type: 'radio',
          label: 'This and all future and past related projects',
          value: '3'
        }
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'OK',
          handler: async (data) => {
            const pauseType = data;
            let apiData: any = await this.apiService.put(`projects/${this.project.guid}/pause/${paused}`, { pause_type: pauseType });
            this.globalService.presentToastMessage(apiData.message, apiData.success);
            if (paused) this.project.paused = 1;
            else this.project.paused = 0;
          }
        }
      ]
    });
    await alert.present();
    return;

  }

  @ViewChild('topRowRef', { static: false }) topRowRef: ElementRef | undefined;
  @ViewChild('footerRowRef', { static: false }) footerRowRef: ElementRef | undefined;
  @ViewChild('navRowRef', { static: false }) navRowRef: ElementRef | undefined;
  GetCommentsSize() {
    const el = this.topRowRef.nativeElement as HTMLElement;
    const rect = el.getBoundingClientRect();
    const footerEl = this.footerRowRef.nativeElement as HTMLElement;
    const footerRect = footerEl.getBoundingClientRect();
    const navEl = this.navRowRef.nativeElement as HTMLElement;
    const navRect = navEl.getBoundingClientRect();
    let allowed_size = Math.round(rect.height) - (Math.round(footerRect.height) + Math.round(navRect.height));
    console.log("Allowed Size:", allowed_size);
    return allowed_size - 1;
  }

  GetProjectRoleName(role) {
    if (role == 1) return "Leader";
    else if (role == 2) return "Worker";
    else if (role == 3) return "Worker (Alt)";
    else if (role == 4) return "Follower";
  }
  async GetTeamMembers() {
    let data: any = await this.apiService.get('team_members');
    if (data.success) {
      this.team_members = data.data;
    }
  }

  async ChangeClientsOrTeamMembers() {
    console.log('Clients or Team Members Changed');

    let membersToCheck: any = [];
    // Shortcut: all team members allowed
    if (this.project.all_team_members) {
      membersToCheck = [];
    }
    else {
      membersToCheck = [...this.project.team_members];
    }

    let team_members_valid = true;

    // Add project team leader to the members to check if one is selected
    if (this.project.team_leader) {
      const teamLeaderGuid = typeof this.project.team_leader === 'string'
        ? this.project.team_leader
        : this.project.team_leader.guid;

      const teamLeaderMember = this.team_members.find(tm => tm.guid === teamLeaderGuid);
      if (teamLeaderMember && !membersToCheck.find(m => m.guid === teamLeaderGuid)) {
        membersToCheck.push(teamLeaderMember);
      }
    }

    for (const teamMember of membersToCheck) {
      // Ensure cache exists
      (teamMember as any).permissionCache ??= {};
      const cache = (teamMember as any).permissionCache as { [key: string]: boolean };
      if (this.project.client_guid) {
        const response: any = await this.apiService.get(
          `team_members/${teamMember.guid}/permissions/${this.project.client_guid}/false`
        );
        if (!response.success) {
          team_members_valid = false;
        }
      }
      else if (this.project.company_guid) {
        const response: any = await this.apiService.get(
          `team_members/${teamMember.guid}/permissions/${this.project.company_guid}/true`
        );
        if (!response.success) {
          team_members_valid = false;
        }
      }
    }

    this.team_members_valid = team_members_valid;
  }




  readonly assignmentSettingsOptions = [
    { value: 3, label: 'Tasks Auto Assigned - Auto-Assign Based on Skills & Availability', description: 'Fusion Workflow will automatically assign one of the specified team members that is assigned to this project to each task based on their skills and schedule availability once the project starts.  However, team members can easily reassign tasks based on their work load as the project progresses as well.' },
    { value: 2, label: 'Tasks Assigned - Specify Team Members', description: 'You specifically assign which team members will work on each task as part of the initial project setup.  However, team members can easily reassign tasks based on availability and work load as the project progresses as well.' },
    { value: 1, label: 'Tasks Unassigned - Team Members Can Claim', description: 'All the Tasks will initially be unassigned when the projects of this type are created and any of the specified team members assigned to this project will be able to assign themselves to each task once the project starts.  However, team members can easily reassign tasks based on availability and work load as the project progresses as well.' },
    { value: 0, label: 'Tasks Unassigned - Only Project Leader Can Assign', description: 'All the Tasks will initially be unassigned when the projects of this type are created and only the project leader will be able to assign team members to each task once the project starts.' }
  ];

  GetAssignmentType(task) {
    if (task.assignment_settings == 0) return "Project Leader Assigns";
    else if (task.assignment_settings == 1) return "Team Memebers can Claim";
    else if (task.assignment_settings == 2) return this.GetTeamMemberDisplayNameByGuid(task.assigned_member_guid);
    else if (task.assignment_settings == 3) return "Auto Assigned";
  }

  GetTeamMemberDisplayNameByGuid(guid) {
    const member = this.team_members.find(tm => tm.guid === guid);
    return member ? member.display_name : '';
  }

  IsAdminOrProjectLeader() {
    if (this.globalService.GetCurrentUser().is_admin) return true;
    if (this.project.team_leader && this.project.team_leader.guid == this.globalService.GetCurrentUser().guid) return true;
    return false;
  }

  async GetTaskTemplates() {
    let data: any = await this.apiService.get('templates/tasks');
    if (data.success) {
      this.task_templates = data.data;
    }
  }

  async LoadFromTaskTemplates() {
    if (this.selected_task_template) {
      let task_data: any = await this.apiService.get(`templates/tasks/${this.selected_task_template.guid}`);
      let task_json: any = JSON.parse(task_data.data[0].json_str);
      task_json.template_guid = this.selected_task_template.guid;
      task_json.guid = this.globalService.createGUID();
      task_json.front_end_guid = this.globalService.createGUID();
      this.ChangeTaskDates(task_json);
      this.project.tasks.push(task_json);
      this.selected_task_template = null;
    }
  }

  async OpenCreateTaskModal() {
    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        project: this.project,
        edit_mode: false
      },
    })
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.project.tasks.push(JSON.parse(JSON.stringify(data.data)));
      }
    });
    return await modal.present()
  }

  async RemoveTask(task) {
    const alert = await this.alertController.create({
      header: 'Remove Task',
      message: 'Are you sure you want to remove this task? ' + task.title,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Remove',
          handler: () => {
            const index = this.project.tasks.findIndex(obj =>
              JSON.stringify(obj) === JSON.stringify(task)
            );
            if (index !== -1)
              this.project.tasks.splice(index, 1);
          }
        }
      ]
    });
    await alert.present();
  }

  async EditTask(task) {
    this.current_edit_task_copy = JSON.stringify(task);
    const modal = await this.modalController.create({
      component: CreatePage,
      cssClass: 'large-modal',
      backdropDismiss: false,
      componentProps: {
        project: this.project,
        task: task,
        edit_mode: true
      },
    })
    modal.onDidDismiss().then((data: any) => {
      console.log(data);
      const taskIndex = this.project.tasks.findIndex(t => t.guid === task.guid);
      if (!data.data) {
        if (taskIndex !== -1) {
          this.project.tasks[taskIndex] = JSON.parse(this.current_edit_task_copy);
        }
      }
      else {
        if (taskIndex !== -1) {
          this.project.tasks[taskIndex] = JSON.parse(JSON.stringify(data.data));
        }
      }
    });
    return await modal.present()
  }

  async RemoveStep(task, step) {
    const index = task.steps.findIndex(obj =>
      JSON.stringify(obj) === JSON.stringify(step)
    );
    if (index !== -1)
      task.steps.splice(index, 1);
  }

  TaskHasActiveSteps(task) {
    let all_active = false;
    if (task.steps && task.steps.length > 0) {
      task.steps.forEach(step => {
        if (step.type == 1 || step.type == 2 || step.type == 3 || step.type == 4 || step.type == 5 || step.type == 6) {
          all_active = true;
        }
      });
    }
    return all_active;
  }

  AllUsersActive() {
    let all_active = true;
    if (this.project.assigned_clients) {
      this.project.assigned_clients.forEach(client => {
        if (client.active == 0) {
          all_active = false;
        }
      });
    }
    if (this.project.assigned_companies) {
      this.project.assigned_companies.forEach(company => {
        if (company.default_client.active == 0) {
          all_active = false;
        }
      });
    }
    return all_active;
  }

  async ChangeProjectDates() {

  }

  async ViewTaskFromComment(comment: any) {
    const modal = await this.modalController.create({
      component: TaskDetailsPage,
      componentProps: { guid: comment.source_guid, project: this.project },
      backdropDismiss: true,
      cssClass: 'large-modal secondary-modal'
    });
    modal.present();
  }

  async ViewStepFromComment(comment: any) {
    const modal = await this.modalController.create({
      component: StepDetailsPage,
      componentProps: { guid: comment.source_guid, project: this.project },
      backdropDismiss: true,
      cssClass: 'large-modal secondary-modal'
    });
    modal.present();
  }

  SolveProjectStatus(project) {
    if(project.status == -1) {
      if(project.progress == 0) return "Inactive"
      else return "Active";
      }
      else if (project.status == 0 || project.status == 2) {
          return 'Active'
      } else if (project.status == 1) {
          return 'Completed'
      } 

    return "";

  }

   async taskModified(eventData: { data: any }) {
    //We are going to find the task, and update it in the task array
    let returned_task_data = eventData.data;
    let data: any = await this.apiService.get(`projects/${this.project.guid}`);
    this.project.progress = data.data[0];
    for (let i = 0; i < data.data[0].tasks.length; i++) {
      this.UpdateTaskInfo(this.project,this.project.tasks[i], data.data[0].tasks[i]);
    }

  }

  async UpdateTaskInfo(project,task, task_data) {
    task.progress = task_data.progress;
    task.status = task_data.status;
    task.project_guid = task_data.project_guid;
    task.completed_date = task_data.completed_date;
    if(!this.IsPreviousTaskCompleted(project,task)) task.status = TaskStatus.awaiting_previous_task;
    
  }

  IsPreviousTaskCompleted(project, task) {
    if (!project || !project.tasks || project.tasks.length === 0) {
      return true;
    }
    task.has_checked_status = true;
    if (!project.force_order) {
      return true;
    }

    const currentTaskIndex = project.tasks.findIndex(t => t.guid === task.guid);
    console.log("Current Task Index: MODIFIED PROJECT", currentTaskIndex);
    if (currentTaskIndex <= 0) {
      return true;
    }

    const previousTask = project.tasks[currentTaskIndex - 1];
    if (previousTask.status == TaskStatus.completed) {
      return true;
    }

    if (previousTask.status != TaskStatus.completed) {
      return false;
    }
    else return true;
  }

  
  private normalizeHexColour(colour: string): string {
    return (colour || '').trim().toUpperCase();
  }

  private syncRowColourSelection() {
    const projectColour = this.normalizeHexColour(this.project?.row_colour || '#FFFFFF');
    const presetMatch = this.rowColourPresets.find(c => this.normalizeHexColour(c) === projectColour);
    if (presetMatch) {
      this.project.row_colour = presetMatch;
      this.isCustomRowColourSelected = false;
      return;
    }

    this.customRowColour = this.project?.row_colour || this.customRowColour || '#FFFFFF';
    this.isCustomRowColourSelected = true;
  }

  isPresetRowColourSelected(color: string): boolean {
    return !this.isCustomRowColourSelected && this.normalizeHexColour(this.project?.row_colour) === this.normalizeHexColour(color);
  }

  onPresetRowColourSelect(color: string) {
    this.project.row_colour = color;
    this.isCustomRowColourSelected = false;
  }

  onCustomRowColourSelect() {
    this.isCustomRowColourSelected = true;
    this.project.row_colour = this.customRowColour || '#FFFFFF';
  }

  onCustomRowColourChange(color: string) {
    this.customRowColour = color || '#FFFFFF';
    if (this.isCustomRowColourSelected) {
      this.project.row_colour = this.customRowColour;
    }
  }






}
